import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { myBillingQuery, myOrdersQuery, myProfileQuery } from "@/lib/queries";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";
import { InvoiceDownloadButton } from "@/components/invoice-download-button";
import { useT } from "@/i18n";

export const Route = createFileRoute("/app/invoices/$id")({
  head: () =>
    privatePageMetadata(
      "/app/invoices",
      "Invoice | BeHot.Now",
      "Invoice detail.",
    ),
  component: InvoiceDetail,
});

function InvoiceDetail() {
  const { t } = useT();
  const { id } = Route.useParams();
  const { user } = useAuth();
  const { data: orders = [], isLoading } = useQuery(myOrdersQuery(user?.id));
  const { data: billing } = useQuery(myBillingQuery(user?.id));
  const { data: profile } = useQuery(myProfileQuery(user?.id));
  const order = orders.find((o) => o.id === id);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">{t("dashboard.invoiceDetail.loading")}</div>;
  }

  if (!order) {
    return (
      <div className="card-surface p-6">
        <p className="font-semibold">{t("dashboard.invoiceDetail.notFound")}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/app/invoices">{t("dashboard.invoiceDetail.backToInvoices")}</Link>
        </Button>
      </div>
    );
  }

  const billTo = [
    billing?.company_name ?? profile?.display_name ?? user?.email,
    billing?.tax_id ? `${t("dashboard.invoiceDetail.taxId")} ${billing.tax_id}` : null,
    billing?.address_line1,
    billing?.address_line2,
    [billing?.postal_code, billing?.city].filter(Boolean).join(" "),
    billing?.country_code,
  ].filter(Boolean) as string[];

  const kind = order.kind ?? "boost";
  const description =
    kind === "topup"
      ? t("dashboard.invoiceDetail.descTopup")
      : kind === "listing_edit"
        ? `${t("dashboard.invoiceDetail.descListingEdit")}${order.listing?.name ? ` — ${order.listing.name}` : ""}`
        : `${t("dashboard.invoiceDetail.descBoost")}${order.listing?.name ? ` — ${order.listing.name}` : ""}${
            order.category ? ` ${t("dashboard.invoiceDetail.onCategory", { category: order.category.name })}` : ""
          }`;

  const subtotal = order.amount_due_cents;
  const total = order.amount_paid_cents || order.amount_due_cents;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between print:hidden">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/invoices">
            <ArrowLeft className="size-4" /> {t("dashboard.invoiceDetail.invoicesLink")}
          </Link>
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => window.print()}>
            <Printer className="size-4" /> {t("dashboard.invoiceDetail.print")}
          </Button>
          <InvoiceDownloadButton orderId={order.id} />
        </div>
      </div>

      <div className="card-surface mt-4 p-8">
        <div className="flex items-start justify-between gap-4 border-b-2 border-primary pb-6">
          <img src="/brand/wordmark-280.png" alt="BeHot.Now" width={280} height={70} className="h-7 w-auto" />
          <p className="font-display text-3xl font-bold uppercase tracking-[0.2em] text-primary">
            {t("dashboard.invoiceDetail.invoice")}
          </p>
        </div>

        <div className="mt-6 grid gap-8 sm:grid-cols-2">
          <div className="text-sm leading-6">
            <p className="font-semibold uppercase">HRN Innovation Technologies Ltd</p>
            <p className="text-muted-foreground">{t("dashboard.invoiceDetail.licence")}</p>
            <p className="text-muted-foreground">{t("dashboard.invoiceDetail.address1")}</p>
            <p className="text-muted-foreground">{t("dashboard.invoiceDetail.address2")}</p>
            <p className="text-muted-foreground">finance-uae@horno.net</p>
          </div>
          <dl className="space-y-1 text-sm">
            {[
              [t("dashboard.invoiceDetail.invoiceNumber"), order.invoice_number ?? order.id.slice(0, 8).toUpperCase()],
              [
                t("dashboard.invoiceDetail.issued"),
                new Date(order.paid_at ?? order.created_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }),
              ],
              [t("dashboard.invoiceDetail.status"), order.status.toUpperCase()],
              [t("dashboard.invoiceDetail.ref"), order.stripe_payment_intent_id ?? order.receipt_number ?? order.id],
              [t("dashboard.invoiceDetail.method"), t("dashboard.invoiceDetail.methodValue")],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-4">
                <dt className="w-24 shrink-0 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {label}
                </dt>
                <dd className="min-w-0 break-all font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t("dashboard.invoiceDetail.billTo")}</p>
          <div className="mt-2 space-y-0.5 text-sm">
            {billTo.map((line) => (
              <p key={line}>{line}</p>
            ))}
            <p className="text-muted-foreground">{billing?.billing_email ?? user?.email}</p>
          </div>
        </div>

        <div className="mt-8 -mx-2 overflow-x-auto px-2">
        <table className="w-full min-w-[480px] text-sm">

          <thead>
            <tr className="bg-primary/10 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="p-3">{t("dashboard.invoiceDetail.description")}</th>
              <th className="p-3 text-right">{t("dashboard.invoiceDetail.qty")}</th>
              <th className="p-3 text-right">{t("dashboard.invoiceDetail.unit")}</th>
              <th className="p-3 text-right">{t("dashboard.invoiceDetail.amount")}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border">
              <td className="p-3">{description}</td>
              <td className="p-3 text-right">1</td>
              <td className="p-3 text-right">{formatMoney(subtotal, order.currency)}</td>
              <td className="p-3 text-right">{formatMoney(subtotal, order.currency)}</td>
            </tr>
            {order.credits_applied_cents > 0 && (
              <tr className="border-b border-border">
                <td className="p-3">{t("dashboard.invoiceDetail.creditsApplied")}</td>
                <td className="p-3 text-right">1</td>
                <td className="p-3 text-right">
                  −{formatMoney(order.credits_applied_cents, order.currency)}
                </td>
                <td className="p-3 text-right">
                  −{formatMoney(order.credits_applied_cents, order.currency)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>


        <div className="mt-6 flex justify-end">
          <dl className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t("dashboard.invoiceDetail.subtotal")}</dt>
              <dd>{formatMoney(subtotal, order.currency)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t("dashboard.invoiceDetail.vat")}</dt>
              <dd>{formatMoney(0, order.currency)}</dd>
            </div>
            <div className="mt-2 flex justify-between border-t-2 border-primary pt-2 font-display text-base font-bold">
              <dt>{t("dashboard.invoiceDetail.totalDue")}</dt>
              <dd>{formatMoney(total, order.currency)}</dd>
            </div>
          </dl>
        </div>

        <div className="mt-10 border-t border-border pt-4 text-xs leading-5 text-muted-foreground">
          <p className="font-semibold text-foreground">{t("dashboard.invoiceDetail.notes")}</p>
          <p>
            {t("dashboard.invoiceDetail.notRefundable")}
          </p>
          <p>
            {t("dashboard.invoiceDetail.taxNotice")}
          </p>
        </div>

        <p className="mt-8 text-center text-[11px] text-muted-foreground">
          {t("dashboard.invoiceDetail.footer")}
        </p>
      </div>
    </div>
  );
}
